/**
 * API client for the CRM code ported from xop-edge-staff.
 *
 * ────────────────────────────────────────────────────────────────────────
 * THIS IS THE ONE FILE OF THE PORT THAT IS NOT A COPY.
 * ────────────────────────────────────────────────────────────────────────
 *
 * Everything else under `src/crm` is xop-edge-staff's CRM verbatim, so it
 * stays diffable against its source. This file cannot be, because the two
 * apps authenticate to crm-api in fundamentally different ways:
 *
 *   xop-edge-staff  the browser holds a short-TTL crm-api token in
 *                   localStorage and sends it as a bearer. On a 401 it
 *                   re-mints one from the dashboard server and retries.
 *
 *   XOP Reseller    the browser holds NOTHING. The session is an httpOnly
 *                   cookie the BFF set (apps/server/src/auth/session.ts),
 *                   and the BFF mints a fresh ~2-minute reseller token
 *                   server-side for every upstream call. No token is ever
 *                   readable by page JavaScript, which takes token theft
 *                   via XSS off the table entirely.
 *
 * So: no `Authorization` header, no localStorage, no re-mint dance. Just
 * `withCredentials` and a different prefix. Requests go to the BFF's
 * `/api/crm/v1/*` passthrough, which maps 1:1 onto crm-api's `/api/v1/*`
 * (apps/server/src/routes/crmProxy.ts) — so every ported page's URLs are
 * unchanged.
 *
 * A 401 here is therefore NOT "your token expired". The BFF answers 401
 * only when the SESSION is gone, and turns a revoked-membership 401 from
 * crm-api into a 403 precisely so the two are distinguishable. 401 ⇒ sign
 * in again; there is nothing to retry.
 */
import axios from 'axios';

import { reportSessionExpired } from '../../auth/sessionExpiry.js';
import { env } from '../../lib/env.js';

/** The BFF prefix that proxies to crm-api's `/api/v1`. */
const CRM_PREFIX = '/api/crm/v1';

/**
 * Absolute URL for a CRM path — used by the handful of ported components
 * that build a URL for an `<img>`, an `<a download>` or a `fetch` rather
 * than going through axios.
 *
 * `env.apiUrl` is "" in this app (same-origin, relative), so this is
 * effectively identity. It is kept because the ported code calls it, and
 * because a future build served from another origin would need it.
 */
export function crmApiUrl(path: string): string {
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(path)) return path;
  const withPrefix = path.startsWith(CRM_PREFIX)
    ? path
    : `${CRM_PREFIX}${path.startsWith('/') ? '' : '/'}${path}`;
  return `${env.apiUrl}${withPrefix}`;
}

/**
 * No-op, kept so the ported code's call sites compile unchanged.
 *
 * In xop-edge-staff this mints a crm-api session token into localStorage
 * before a call that needs one. Here the BFF mints one per request from the
 * httpOnly session, so there is nothing for the browser to arrange — and
 * nothing it *could* arrange, since it holds no credential.
 */
export async function ensureCrmSession(): Promise<void> {
  /* intentionally empty — see the note above */
}

const api = axios.create({
  baseURL: env.apiUrl,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' },
  // The session cookie is httpOnly and SameSite=Lax; axios omits cookies
  // unless told to. Without this every CRM call is unauthenticated.
  withCredentials: true,
});

// Prefix every request with the BFF's CRM passthrough. The ported code calls
// `api.get('/customers')`, exactly as it does in the command center — only
// the prefix differs, so no page needed editing.
api.interceptors.request.use(
  (config) => {
    if (config.url && !config.url.startsWith(CRM_PREFIX)) {
      config.url = `${CRM_PREFIX}${config.url.startsWith('/') ? '' : '/'}${config.url}`;
    }
    return config;
  },
  (error) => {
    // Never log the error object — it can carry request bodies.
    // eslint-disable-next-line no-console
    console.error('api_request_error');
    return Promise.reject(error);
  },
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;

    // 401 means the SESSION is gone — there is no token to refresh, so the
    // only honest response is to send the user back to sign in. The BFF
    // deliberately maps a revoked-membership 401 from crm-api to 403 so that
    // case does NOT land here and bounce the user into a sign-in loop they
    // cannot escape (signing in would fail the same membership check).
    //
    // Two things this deliberately does NOT do:
    //
    //   It does not navigate. `window.location.assign('/login')` was a FULL
    //   page load, so it could not carry router state, and the page the
    //   reseller was on was lost — a session that expired during
    //   `/invoices/generate?invoiceId=31` came back to `/`. Reporting instead
    //   lets `auth/sessionExpiry`'s bridge navigate through the router with
    //   `from`, the same channel `RequireAuth` already uses.
    //
    //   It does not act on a 401 from anywhere else. `error.config.url` is the
    //   URL the request interceptor above built, so requiring the passthrough
    //   prefix is what confines this to the one surface whose 401 carries the
    //   BFF's meaning. An error with no request of ours behind it (no config,
    //   or another origin's) is just a failure, and the caller handles it.
    const url = error.config?.url;
    if (status === 401 && typeof url === 'string' && url.startsWith(CRM_PREFIX)) {
      reportSessionExpired();
    }

    return Promise.reject(error);
  },
);

export default api;

// Stats API methods (for navigation badges)
export const statsApi = {
  getStats: () => api.get('/dashboard/stats'),
};

// Financial Metrics API methods.
//
// Kept so the ported modules that import it compile. crm-api's reseller
// route allowlist does not grant `/financial-metrics/*`, so these 403 —
// which is correct: those are the tenant's books, not a reseller's. The
// portal does not link to them.
export const financialApi = {
  getOverview: (params?: { period?: string }) =>
    api.get('/financial-metrics/overview', { params }),

  getSalespersonDetails: (userId: string, params?: { period?: string }) =>
    api.get(`/financial-metrics/salesperson-details/${userId}`, { params }),

  getLeaderboard: (params?: { period?: string; sort_by?: string }) =>
    api.get('/financial-metrics/leaderboard', { params }),

  getProductPerformance: (params?: { period?: string }) =>
    api.get('/financial-metrics/product-performance', { params }),

  exportPDF: (params?: { period?: string; user_id?: string }) =>
    api.post('/financial-metrics/export-pdf', params),
};
